// Blend Discovery Platform — app entry.
// AntD ConfigProvider drives off `theme.ts` (algorithm-based light/dark).
// Custom (non-AntD) CSS custom properties are defined statically in `_theme-vars.scss`;
// dark-mode overrides kick in via `<html data-theme="dark">`.
import { ConfigProvider } from 'antd';
import React from 'react';
import ReactDOM from 'react-dom/client';

import '@fortawesome/fontawesome-free/css/all.min.css';
import { App } from '@/App';
import { useThemeMode } from '@/hooks/useThemeMode';
import { themesByMode } from '@/styles/theme/theme';
import { createLogger } from '@/utils/logging';
import '@/styles/typography.css';
import '@/styles/main.scss';

(globalThis as { __APP_ENV__?: Record<string, string | undefined> }).__APP_ENV__ = import.meta
  .env as Record<string, string | undefined>;

createLogger('bdp-frontend', 'main').info(`[BUILD] ${__BUILD_TIME__}`, {
  apiUrl: import.meta.env.VITE_API_URL,
});

const root = document.getElementById('root');

if (!root) {
  throw new Error('Failed to find the root element');
}

function ThemedApp(): React.ReactElement {
  const { resolvedMode } = useThemeMode();

  React.useEffect(() => {
    document.documentElement.dataset.theme = resolvedMode;
  }, [resolvedMode]);

  return (
    <ConfigProvider theme={themesByMode[resolvedMode]}>
      <App />
    </ConfigProvider>
  );
}

ReactDOM.createRoot(root).render(
  <React.StrictMode>
    <ThemedApp />
  </React.StrictMode>,
);
